// Importing necessary components and hooks
import InputError from "component/InputError/InputError";
import { IBaseInputCommonProps } from "component/interface/Input";
import Label from "component/Label/Label";
import Text from "component/Text/Text";
import { withAuth } from "hoc/withAuth";
import { InputText, InputTextProps } from "primereact/inputtext";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
// Importing icons
import { CheckCircleIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import eyeClosedIcon from "asset/img/icons/eye-closed.svg";
import eyeOpenedIcon from "asset/img/icons/eye-opened.svg";

export interface IBaseInputProps extends InputTextProps, IBaseInputCommonProps {
  hideLabel?: boolean;
  label?: string;
  labelClassNames?: string;
  labelStyleObj?: React.CSSProperties;
  hideErrorRow?: boolean;
  errorClassNames?: string;
  errorStyleObj?: React.CSSProperties;
  disabled?: boolean;
  isDisabledByAcl?: boolean;
  isIgnoreAccessLevel?: boolean;
  rightCTA?: React.ReactNode | string;
  onClickRightCTA?: () => void;
  args?: any;
  enableValueCopy?: boolean;
  suffix?: string;
}

const BaseInput: React.FC<IBaseInputProps> = forwardRef(
  (props: IBaseInputProps, ref?: React.ForwardedRef<HTMLInputElement>) => {
    const {
      args,
      errorClassNames,
      errorStyleObj,
      hideErrorRow,
      hideLabel,
      isDisabledByAcl,
      isIgnoreAccessLevel,
      label,
      labelClassNames,
      labelStyleObj,
      mandatory,
      onClickRightCTA,
      rightCTA,
      validation,
      info,
      enableValueCopy,
      suffix,
      ...PrimeReactProps
    } = props;

    const { placeholder, className, value, style, name, onChange, type, disabled } =
      PrimeReactProps;

    const [visible, setVisible] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isCopyButtonHovered, setIsCopyButtonHovered] = useState(false);
    const { t } = useTranslation();
    const translatedPlaceholder = t(placeholder!, args);

    const copyToClipboard = () => {
      if (!navigator?.clipboard?.writeText) {
        return;
      }

      if (typeof value === "string") {
        navigator.clipboard.writeText(value).then(() => {
          setIsCopied(true);

          setTimeout(() => {
            setIsCopied(false);
          }, 5000);
        });
      }
    };

    return (
      <div className="w-full">
        {!hideLabel && (
          <div className="flex flex-row">
            <Label
              label={label}
              info={info}
              labelClassNames={labelClassNames}
              labelStyleObj={labelStyleObj}
              isInvalid={validation}
              mandatory={mandatory}
              disabled={disabled}
            />
            {rightCTA && (
              <button
                className="mb-1 ml-auto cursor-pointer text-body-copy-2 text-primary-link-btn-color hover:underline"
                onClick={onClickRightCTA}
              >
                {""}
                <Text label={rightCTA} />
              </button>
            )}
          </div>
        )}
        <div className="group relative">
          <InputText
            {...PrimeReactProps}
            className={
              className + `${type === "password" || enableValueCopy || suffix ? "pr-10" : ""}`
            }
            value={value}
            style={style}
            name={name}
            placeholder={translatedPlaceholder}
            onChange={onChange}
            disabled={disabled}
            type={type === "password" && visible ? "text" : type}
          />

          {suffix && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 transform text-body-copy-1">
              {suffix}
            </div>
          )}

          {type === "password" && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 transform cursor-pointer"
              onClick={() => {
                setVisible(!visible);
              }}
              disabled={disabled}
            >
              <img src={visible ? eyeClosedIcon : eyeOpenedIcon} alt="eye" />
            </button>
          )}

          {enableValueCopy && value ? (
            <>
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform cursor-pointer text-left text-body-copy-1 opacity-0 transition-opacity group-hover:opacity-100"
                onClick={copyToClipboard}
                onMouseEnter={() => setIsCopyButtonHovered(true)}
                onMouseLeave={() => setIsCopyButtonHovered(false)}
                aria-label={isCopied ? "Copied" : "Click to copy"}
                type="button"
              >
                <div className="h-4 w-4 text-light-grey-700 dark:text-dark-neutral-gray-700">
                  {!isCopied ? <DocumentDuplicateIcon /> : <CheckCircleIcon />}
                </div>
              </button>

              {isCopyButtonHovered && (
                <div
                  className={`absolute -top-7.5 right-0 flex max-w-fit items-center rounded-md bg-light-grey-300 p-2 text-xs leading-none dark:bg-dark-neutral-gray-400/100 ${
                    isCopied
                      ? "text-primary-into-the-green-700 dark:text-primary-green-onion-800"
                      : "text-light-grey-1000 dark:text-dark-neutral-gray-1000"
                  }`}
                >
                  <span>{t(isCopied ? "Copied!" : "Click to copy")}</span>
                </div>
              )}
            </>
          ) : null}
        </div>

        {!hideErrorRow && (
          <InputError
            validation={validation}
            hideErrorRow={hideErrorRow}
            errorClassNames={errorClassNames + " mt-1"}
          />
        )}
      </div>
    );
  }
);

export default withAuth(BaseInput);
